import { Link, useLocation, useNavigate } from "@remix-run/react";
import { Input } from "@core/components/ui/Input";
import { Button } from "@core/components/ui/button";
import { LoadingOverlay } from "@core/components/ui/loading-overlay";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { AlertDescription, AlertTitle } from "@core/components/ui/alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Surface } from "@core/components/ui/surface";
import { useAuthActions } from "@core/hooks/useAuthActions";
import { Controller, Form, FormProvider, useForm } from "react-hook-form";
import { ErrorMessage } from "@core/components/forms/ErrorMessage";
import { LoginRequest, LoginRouteConfigSection } from "@/apidtos";
import { Label } from "@core/components/ui/label";
import { useTranslation } from "react-i18next";
import { useRootLoaderData } from "@/root";
import { useEffect, useState } from "react";
import { toast } from "@core/components/ui/use-toast";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { faEye, faEyeSlash } from "@fortawesome/pro-light-svg-icons";
import Spinner from "@core/components/ui/Spinner";
import ExtensionPoint from "@core/components/PipelineComponents/ExtensionPoint";
import { useExtensionRegistry } from "@core/components/PipelineComponents/ExtensionProvider";
import { useAuthConfig } from "@core/hooks/useAuthConfig";
import { LabeledPhoneNumber } from "@core/components/forms/LabeledPhoneNumber";
import { ReplaceIfExtension } from "@core/components/PipelineComponents/ReplaceIfExtension";

interface ILoginForm extends LoginRequest {
  PhoneNumber?: string;
  // digitPlusIndex won't ever be a boolean, just making TS be quiet
  [digitPlusIndex: string]: string | undefined | boolean;
}

interface LoginFormProps {
  includeLabels?: boolean;
  linkClasses?: string;
  linkContainerClasses?: string;
  loginLabel?: string;
  onClose?: () => void;
  onSuccess?: () => void;
  skipSubmit?: boolean;
  /** Resolved destination after login (return URL, post-login route, or /). */
  redirectTo?: string;
}

type MFAMethod = "Email" | "SMS";

export const LoginForm = ({ includeLabels, linkClasses, linkContainerClasses, loginLabel, onClose, onSuccess, skipSubmit, redirectTo }: LoginFormProps) => {
  const form = useForm<ILoginForm>();
  const { config } = useRootLoaderData();
  const { user } = useCurrentUser();
  const { login } = useAuthActions();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginConfig = config.Login as LoginRouteConfigSection;
  const postLoginRoute = redirectTo ?? loginConfig.PostLoginRoute ?? "/";
  const authConfig = useAuthConfig();
  const { getExtensions } = useExtensionRegistry();
  const [mfaRequired, setMfaRequired] = useState(false);
  const [credentials, setCredentials] = useState<LoginRequest | null>(null);
  const [mfaFailed, setMfaFailed] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<MFAMethod | null>(null);
  const [mfaMethodsAvailable, setMfaMethodsAvailable] = useState<MFAMethod[]>([]);
  const [phoneLast4, setPhoneLast4] = useState<string | null>(null);
  const [mfaCodeSent, setMfaCodeSent] = useState(false);
  const [resendCooldownRemaining, setResendCooldownRemaining] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const typedInUsername = form.watch("Username");
  const typedInPhoneNumber = form.watch("PhoneNumber");

  const applyChallengeResponse = (res: unknown) => {
    const payload = (res ?? {}) as Record<string, unknown>;
    const methods = Array.isArray(payload.MFAMethodsAvailable)
      ? (payload.MFAMethodsAvailable as MFAMethod[])
      : [];
    const selectedMethod = typeof payload.MFAMethod === "string"
      ? (payload.MFAMethod as MFAMethod)
      : (methods.length === 1 ? methods[0] : null);
    const cooldown = typeof payload.MFAResendCooldownSeconds === "number"
      ? payload.MFAResendCooldownSeconds
      : 30;
    setMfaMethodsAvailable(methods);
    setMfaMethod(selectedMethod);
    setPhoneLast4(typeof payload.PhoneLast4 === "string" ? payload.PhoneLast4 : null);
    setResendCooldownRemaining(cooldown);
    if (selectedMethod === "Email") {
      setMfaCodeSent(true);
    }
  };

  const resendMFACode = async (options?: { suppressToast?: boolean }) => {
    try {
      setMfaFailed(false);
      if (credentials) {
        if (!mfaMethod) {
          return;
        }
        const res = await login.mutateAsync({
          body: {
            ...credentials,
            MFAMethod: mfaMethod,
            SendMFACode: mfaMethod === "SMS" ? true : undefined,
          }
        });
        if (res?.Success?.toString() === "Challenge") {
          applyChallengeResponse(res);
          setMfaCodeSent(true);
          Array.from({ length: 6 }, (_, i) => form.setValue(`digit${i}`, ""));
          form.setValue("Token", "");
          if (!options?.suppressToast) {
            toast({
              title: "New Code Sent",
              description: mfaMethod === "SMS"
                ? "A new verification code has been sent to your phone."
                : "A new verification code has been sent to your email.",
              variant: "default",
            });
          }
        } else {
          toast({
            title: "Failed to Send Code",
            description: res?.Message || "Unable to send new verification code. Please try again.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while sending the MFA code.",
        variant: "destructive",
      });
    }
  };


  useEffect(() => {
    if (Boolean(user?.Id)) {
      // Hard navigation so root loaders / auth-dependent UI fully refresh after login.
      window.location.assign(postLoginRoute);
    }
  }, [postLoginRoute, user]);

  useEffect(() => {
    if (resendCooldownRemaining <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendCooldownRemaining((curr) => (curr > 0 ? curr - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldownRemaining]);

  useEffect(() => {
    if (!mfaRequired || mfaMethod !== "SMS" || mfaCodeSent || !credentials || login.isPending) {
      return;
    }

    void resendMFACode({ suppressToast: true });
  }, [mfaRequired, mfaMethod, mfaCodeSent, credentials, login.isPending]);

  const handleMfaConfirm = async () => {
    if (!mfaCodeSent || !mfaMethod || login.isPending) {
      return;
    }
    setMfaFailed(false);
    const mfaRes = await login.mutateAsync({
      body: {
        ...credentials,
        Username: credentials?.Username ?? "",
        Password: credentials?.Password ?? "",
        Token: form.getValues("Token"),
        AuthProvider: credentials?.AuthProvider ?? "",
        MFAMethod: mfaMethod ?? undefined,
        skipSubmit: skipSubmit
      }
    });
    if (mfaRes?.Success?.toString() === "Success") {
      toast({
        title: "Success",
        description: "Login successful",
        variant: "default",
      });
      if (onClose) {
        onClose();
      }
      if (onSuccess) {
        onSuccess();
      }
      window.location.assign(postLoginRoute);
    } else {
      setMfaFailed(true);
      toast({
        title: "MFA Failed",
        description: mfaRes?.Message === "Invalid MFA code"
          ? "The MFA code is incorrect or expired. Please try again or request a new code."
          : mfaRes?.Message || "MFA verification failed.",
        variant: "destructive",
      });
    }
  };

  if (user?.Id) {
    return null;
  }

  let ssoButtons = getExtensions("login.after");
  ssoButtons = ssoButtons.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <div className="relative">
      {login.isPending && <LoadingOverlay />}
      {mfaRequired ? (
        <div className="space-y-4">
          <p className="text-center mx-auto max-w-sm">
            {mfaMethodsAvailable.length > 1 && !mfaMethod
              ? "Choose how to receive your verification code."
              : mfaMethod === "SMS"
                ? `We'll send a verification code to ***-***-${phoneLast4 ?? "----"}.`
                : "An authentication code has been sent to your email. Please enter the code below to continue login."}
          </p>
          {mfaMethodsAvailable.length > 1 && !mfaMethod && (
            <div className="flex justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={login.isPending}
                onClick={async () => {
                  if (!credentials) {
                    return;
                  }

                  const res = await login.mutateAsync({
                    body: {
                      ...credentials,
                      MFAMethod: "Email",
                    }
                  });
                  if (res?.Success?.toString() === "Challenge") {
                    setMfaMethod("Email");
                    applyChallengeResponse(res);
                    setMfaCodeSent(true);
                  }
                }}
              >
                Send via Email
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={login.isPending}
                onClick={async () => {
                  if (!credentials) {
                    return;
                  }

                  const res = await login.mutateAsync({
                    body: {
                      ...credentials,
                      MFAMethod: "SMS",
                      SendMFACode: true,
                    }
                  });
                  if (res?.Success?.toString() === "Challenge") {
                    setMfaMethod("SMS");
                    applyChallengeResponse(res);
                    setMfaCodeSent(true);
                  }
                }}
              >
                Send via SMS (***-***-{phoneLast4 ?? "----"})
              </Button>
            </div>
          )}
          {mfaMethod === "SMS" && !mfaCodeSent && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={login.isPending || resendCooldownRemaining > 0}
                onClick={resendMFACode}
              >
                {resendCooldownRemaining > 0
                  ? `Send Code (${resendCooldownRemaining}s)`
                  : "Send Code"}
              </Button>
            </div>
          )}
          {mfaCodeSent && (
            <>
          <div className="flex justify-center gap-4">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <Input
                key={index}
                type="text"
                maxLength={1}
                data-testid={`login-mfa-digit-${index}`}
                className="w-12 h-12 text-center text-lg font-mono border-2 rounded-lg"
                {...form.register(`digit${index}`, { 
                  onChange: (e) => {
                    const value = e.target.value;
                    if (value && /^\d$/.test(value)) {
                      const nextInput = document.querySelector(`input[name="digit${index + 1}"]`) as HTMLInputElement;
                      if (nextInput) nextInput.focus();
                    }
                    const allDigits = Array.from({length: 6}, (_, i) => 
                      form.getValues(`digit${i}`) || ''
                    ).join('');
                    form.setValue('Token', allDigits);
                  }
                })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleMfaConfirm();
                    return;
                  }
                  if (e.key === 'Backspace' && !e.currentTarget.value) {
                    const prevInput = document.querySelector(`input[name="digit${index - 1}"]`) as HTMLInputElement;
                    if (prevInput) prevInput.focus();
                  }
                }}
              />
            ))}
          </div>
            </>
          )}
          {mfaFailed && (
            <div className="flex flex-col gap-y-2 items-center justify-center">
              <div className="text-danger-500 text-sm font-bold text-center"><FontAwesomeIcon icon={faCircleExclamation} /> {t("ui.invalidMFA", "Invalid verification code")}</div>
            <Button
              type="button"
              variant="ghost"
              onClick={resendMFACode}
              disabled={login.isPending || resendCooldownRemaining > 0 || !mfaMethod}
              className="w-1/4 hover:bg-primary-50 font-bold text-primary-500"
            >
              {resendCooldownRemaining > 0 ? `Send Another Code (${resendCooldownRemaining}s)` : "Send Another Code"}
            </Button>
            </div>
          )}
          <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            className="w-1/4"
            data-testid="login-mfa-confirm"
            disabled={login.isPending || !mfaCodeSent || !mfaMethod}
            onClick={() => void handleMfaConfirm()}
          >
            {login.isPending ? (
                <>
                  <Spinner className="mr-2" />
                  {t("ui.submitting", "Submitting...")}
                </>
              ) : (
                <>
                  {t("ui.confirm", "Confirm")}
                </>
              )}
          </Button>
          </div>
          
        </div>
      ) : (
        <FormProvider {...form}>
          <Form
            control={form.control}
            className="space-y-4"
            onSubmit={async ({ data }) => {
              if (!data.Username && !data.PhoneNumber) {
                return;
              }
              try {
                const loginRes = await login.mutateAsync({
                  body: {
                    ...data,
                    Username: data.Username || data.PhoneNumber,
                    skipSubmit
                  } as LoginRequest
                });
                if (loginRes?.Success?.toString() === "Fail") {
                  const errMessage = loginRes?.Message?.length
                    ? loginRes.Message
                    : "Login failed. Please try again.";
                  toast({
                    title: "Error",
                    description: errMessage,
                    variant: "destructive",
                  });
                  return;
                }
                if (loginRes?.Success?.toString() === "Challenge") {
                  setMfaRequired(true);
                  setMfaFailed(false);
                  setCredentials({
                    ...(data as LoginRequest),
                    Username: (data.Username || data.PhoneNumber) as string,
                  });
                  applyChallengeResponse(loginRes);
                  return;
                }
                toast({
                  title: "Success",
                  description: "Login successful",
                  variant: "default",
                });
                if (onClose) {
                  onClose();
                }
                if (onSuccess) {
                  onSuccess();
                }
                window.location.assign(postLoginRoute);
              } catch (error) {
                console.error("Login failed:", error);
                const errMessage = error instanceof Error
                  ? error.message
                  : "Login failed. Please try again.";
                toast({
                  title: "Error",
                  description: errMessage,
                  variant: "destructive",
                });
              }
            }}
          >
            <div>
              <ReplaceIfExtension name="login.username">
                <Label className="text-neutral-700 text-sm mb-2">
                  {t("ui.Username", "Username")}
                </Label>
                <Input
                  required={!authConfig.config.SignInWithPhoneEnabled}
                  type="text"
                  data-testid="login-username"
                  placeholder={t("ui.EnterYourUsername", "Enter your username")}
                  className="w-full bg-neutral-50 border border-neutral-200 rounded-md p-2"
                  {...form.register(
                    "Username",
                    {
                      required: authConfig.config.SignInWithPhoneEnabled ? undefined : "Username is required",
                      validate: () => {
                        // if either one is not null, everything is valid
                        if (typedInUsername || typedInPhoneNumber) {
                          return undefined;
                        }
                        // if signing in with the phone is enabled, include 'or phone number' in the error message.
                        if (authConfig.config.SignInWithPhoneEnabled) {
                          return t("ui.mustEnterUserNameOrPhone", "Must enter username or phone number");
                        }
                        return t("ui.usernameIsRequired", "Username is required");
                      }
                    }
                  )}
                />
                <ErrorMessage errors={form.formState.errors} name="Username" />
              </ReplaceIfExtension>
              {authConfig.config.SignInWithPhoneEnabled && (
                <>
                  <div className="lowercase text-xs w-full text-center mt-2">{t("ui.or", "or")}</div>
                  <Controller
                    control={form.control}
                    name="PhoneNumber"
                    render={({ field }) => (
                      <LabeledPhoneNumber
                        label={t("ui.phoneNumber", "Phone Number")}
                        inputClassName="w-full bg-neutral-50 border border-neutral-200 rounded-md p-2"
                        onMaskedValueChanged={(masked, _unmasked) => {
                          field.onChange(masked);
                        }}
                        {...field}
                      />
                    )}
                  />
                  <ErrorMessage errors={form.formState.errors} name="PhoneNumber" />
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-neutral-700 text-sm">
                {t("ui.Password", "Password")}
              </Label>
              <div className="spg-login-password-field relative w-full">
                <Input
                  required
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password"
                  placeholder={t("ui.Password", "Enter your password")}
                  className="w-full bg-neutral-50 border border-neutral-200 rounded-md p-2 pr-12"
                  autoComplete="current-password"
                  {...form.register("Password", { required: "Password is required" })}
                />
                <button
                  type="button"
                  className="spg-login-password-toggle absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 p-0 bg-transparent text-[#0e1a2c] hover:text-black cursor-pointer"
                  data-testid="login-password-toggle"
                  aria-label={showPassword
                    ? t("ui.HidePassword", "Hide password")
                    : t("ui.ShowPassword", "Show password")}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} fixedWidth />
                </button>
              </div>
              <ErrorMessage errors={form.formState.errors} name="Password" />
            </div>
            <div className="flex items-end justify-between mt-6 items-center">
              <div className="flex flex-col mr-4">
                <Link
                  to="/auth/forgot"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose?.();
                    navigate('/auth/forgot');
                  }}
                  className="text-primary-600 text-sm hover:underline"
                >
                  Login Assistance
                </Link>
                {/* SPG: Registration link temporarily hidden.
                {(config.Registration as { AllowUserRegistration?: boolean })?.AllowUserRegistration === true && (
                  <Link
                    to="/registration"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose?.();
                      navigate('/registration');
                    }}
                    className="text-primary-600 text-sm hover:underline"
                  >
                    {t("ui.ClickHereToRegister", "Need to create an account?")}
                  </Link>
                )}
                */}
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={login.isPending}
                data-testid="login-submit"
                className="flex items-center gap-2.5 rounded-full font-bold uppercase tracking-wide"
              >
                <img
                  src="/api/file/key/shoe-new.png"
                  alt=""
                  className="h-8 w-auto shrink-0 object-contain"
                  aria-hidden
                />
                LET'S GO!
              </Button>
            </div>
            {login.isError && (
              <Surface variant="danger" className="mt-4 p-4 rounded-md bg-danger-50 border border-danger-200">
                <AlertTitle className="font-bold text-danger-700">
                  {t("ui.Error", "Error")}
                </AlertTitle>
                <AlertDescription className="text-danger-600">
                  {t("ui.UsernameOrPasswordIsIncorrectPleaseTryAgain",
                    "Username or password is incorrect. Please try again.")}
                </AlertDescription>
              </Surface>
            )}
          </Form>
        </FormProvider>
      )}
      <ExtensionPoint name="login.after" />
    </div>
  );
};
